/**
 * ApprovalActionModal Component
 * Modal for managers to approve or reject OKRs with comments
 */

import React, { useState, useEffect } from 'react';
import { RadioGroup } from '@headlessui/react';
import { CheckCircle, XCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { Modal, Button, Textarea } from '../common';
import { useReviewApproval } from '../../hooks';
import type { OKR } from '../../types/okr';

export interface ApprovalActionModalProps {
  okr: OKR;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type ApprovalAction = 'approved' | 'rejected';

export const ApprovalActionModal: React.FC<ApprovalActionModalProps> = ({
  okr,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [action, setAction] = useState<ApprovalAction>('approved');
  const [comments, setComments] = useState('');
  const [validationError, setValidationError] = useState('');

  const { mutate: reviewApproval, isPending } = useReviewApproval();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setAction('approved');
      setComments('');
      setValidationError('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    // Validation: Comments required for rejection
    if (action === 'rejected' && !comments.trim()) {
      setValidationError('Comments are required when rejecting an OKR');
      return;
    }

    // Clear validation error
    setValidationError('');

    // Submit review
    reviewApproval(
      {
        okrId: okr.id,
        action,
        comments: comments.trim() || undefined,
      },
      {
        onSuccess: () => {
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        },
      }
    );
  };

  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Enforce max length
    if (value.length <= 1000) {
      setComments(value);
      // Clear validation error when user starts typing
      if (validationError) {
        setValidationError('');
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Review OKR: ${okr.title}`} size="lg">
      <div className="space-y-6">
        {/* Decision Section */}
        <div>
          <label className="label mb-3 block">Decision</label>
          <RadioGroup value={action} onChange={setAction}>
            <div className="space-y-2">
              <RadioGroup.Option value="approved">
                {({ checked }) => (
                  <div
                    className={clsx(
                      'flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-colors',
                      checked
                        ? 'border-success-600 bg-success-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <CheckCircle
                      className={clsx('h-6 w-6', checked ? 'text-success-600' : 'text-gray-400')}
                    />
                    <div className="flex-1">
                      <p className={clsx('font-medium', checked ? 'text-success-900' : 'text-gray-900')}>
                        Approve
                      </p>
                      <p className="text-sm text-gray-600">
                        This OKR meets the criteria and can be activated
                      </p>
                    </div>
                  </div>
                )}
              </RadioGroup.Option>

              <RadioGroup.Option value="rejected">
                {({ checked }) => (
                  <div
                    className={clsx(
                      'flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-colors',
                      checked
                        ? 'border-danger-600 bg-danger-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <XCircle
                      className={clsx('h-6 w-6', checked ? 'text-danger-600' : 'text-gray-400')}
                    />
                    <div className="flex-1">
                      <p className={clsx('font-medium', checked ? 'text-danger-900' : 'text-gray-900')}>
                        Reject
                      </p>
                      <p className="text-sm text-gray-600">
                        This OKR needs revisions before it can be approved
                      </p>
                    </div>
                  </div>
                )}
              </RadioGroup.Option>
            </div>
          </RadioGroup>
        </div>

        {/* Comments Section */}
        <div>
          <Textarea
            label="Comments"
            placeholder={
              action === 'approved'
                ? 'Add feedback or encouragement (optional)...'
                : 'Explain what needs to be changed (required)...'
            }
            value={comments}
            onChange={handleCommentsChange}
            maxLength={1000}
            error={validationError}
            helperText={`${comments.length}/1000 characters`}
            required={action === 'rejected'}
            rows={4}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 border-t pt-4">
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={action === 'approved' ? 'primary' : 'danger'}
            onClick={handleSubmit}
            isLoading={isPending}
            disabled={isPending}
          >
            {isPending
              ? action === 'approved'
                ? 'Approving...'
                : 'Rejecting...'
              : action === 'approved'
              ? 'Approve OKR'
              : 'Reject OKR'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
